/**
 * Owner: Masa Bayu
 * File: world-languages.ts
 * Date:Time: 2026-10-29 11:10
 * Description: §418 — registri nama bahasa ISO 639-1 (tag + endonim + nama
 *   English) untuk pemilih bahasa dunia. HANYA nama: arah tulisan & skrip
 *   didedahkan oleh resolveDirectionHint; status terjemahan UI oleh katalog.
 */
import { normalizeLanguageId } from './language-id';
/**
 * ISO 639-1 [tag, nama English, endonim]. Endonim = draf, menunggu semakan Owner.
 * Tag disimpan sebagai `primary` hasil normalizeLanguageId enjin — satu-satunya
 * perbezaan diukur: 'tl' → 'fil' (tag lama dilaporkan `deprecatedFrom` oleh enjin).
 */
const ISO_639_1 = Object.freeze([
    ['aa', 'Afar', 'Qafar af'],
    ['ab', 'Abkhazian', 'Аҧсуа'],
    ['ae', 'Avestan', 'avesta'],
    ['af', 'Afrikaans', 'Afrikaans'],
    ['ak', 'Akan', 'Akana'],
    ['am', 'Amharic', 'አማርኛ'],
    ['an', 'Aragonese', 'Aragonés'],
    ['ar', 'Arabic', 'العربية'],
    ['as', 'Assamese', 'অসমীয়া'],
    ['av', 'Avaric', 'авар мацӀ'],
    ['ay', 'Aymara', 'aymar aru'],
    ['az', 'Azerbaijani', 'azərbaycan dili'],
    ['ba', 'Bashkir', 'башҡорт теле'],
    ['bi', 'Bislama', 'Bislama'],
    ['bm', 'Bambara', 'bamanankan'],
    ['bn', 'Bengali', 'বাংলা'],
    ['bo', 'Tibetan', 'བོད་སྐད་'],
    ['br', 'Breton', 'brezhoneg'],
    ['bs', 'Bosnian', 'bosanski jezik'],
    ['ca', 'Catalan', 'català'],
    ['ce', 'Chechen', 'нохчийн мотт'],
    ['ch', 'Chamorro', 'Chamoru'],
    ['co', 'Corsican', 'corsu'],
    ['cr', 'Cree', 'ᓀᐃᐍᐏᐣ'],
    ['cs', 'Czech', 'čeština'],
    ['cu', 'Church Slavic', 'словѣньскъ ѩзꙑкъ'],
    ['cv', 'Chuvash', 'чӑвашла'],
    ['cy', 'Welsh', 'Cymraeg'],
    ['da', 'Danish', 'dansk'],
    ['de', 'German', 'Deutsch'],
    ['dv', 'Dhivehi', 'ދިވެހި'],
    ['dz', 'Dzongkha', 'རྫོང་ཁ'],
    ['ee', 'Ewe', 'Eʋegbe'],
    ['el', 'Greek', 'ελληνικά'],
    ['en', 'English', 'English'],
    ['eo', 'Esperanto', 'Esperanto'],
    ['es', 'Spanish', 'español'],
    ['et', 'Estonian', 'eesti'],
    ['eu', 'Basque', 'euskara'],
    ['fa', 'Persian', 'فارسی'],
    ['ff', 'Fulah', 'Fulfulde'],
    ['fi', 'Finnish', 'suomi'],
    ['fj', 'Fijian', 'vosa Vakaviti'],
    ['fo', 'Faroese', 'føroyskt'],
    ['fr', 'French', 'français'],
    ['fy', 'Western Frisian', 'Frysk'],
    ['ga', 'Irish', 'Gaeilge'],
    ['gd', 'Scottish Gaelic', 'Gàidhlig'],
    ['gl', 'Galician', 'galego'],
    ['gu', 'Gujarati', 'ગુજરાતી'],
    ['gv', 'Manx', 'Gaelg'],
    ['ha', 'Hausa', 'Hausa'],
    ['he', 'Hebrew', 'עברית'],
    ['hi', 'Hindi', 'हिन्दी'],
    ['ho', 'Hiri Motu', 'Hiri Motu'],
    ['hr', 'Croatian', 'hrvatski'],
    ['ht', 'Haitian', 'Kreyòl ayisyen'],
    ['hu', 'Hungarian', 'Magyar'],
    ['hy', 'Armenian', 'Հայերեն'],
    ['hz', 'Herero', 'Otjiherero'],
    ['ia', 'Interlingua', 'Interlingua'],
    ['id', 'Indonesian', 'Bahasa Indonesia'],
    ['ie', 'Interlingue', 'Interlingue'],
    ['ig', 'Igbo', 'Asụsụ Igbo'],
    ['ii', 'Nuosu', 'ꆈ꒿ Nuosuhxop'],
    ['ik', 'Inupiaq', 'Iñupiaq'],
    ['io', 'Ido', 'Ido'],
    ['is', 'Icelandic', 'Íslenska'],
    ['it', 'Italian', 'italiano'],
    ['iu', 'Inuktitut', 'ᐃᒃᑎᑐᑦ'],
    ['ja', 'Japanese', '日本語'],
    ['jv', 'Javanese', 'basa Jawa'],
    ['ka', 'Georgian', 'ქართული'],
    ['kg', 'Kongo', 'Kikongo'],
    ['ki', 'Kikuyu', 'Gĩkũyũ'],
    ['kj', 'Kuanyama', 'Kuanyama'],
    ['kk', 'Kazakh', 'қазақ тілі'],
    ['km', 'Central Khmer', 'ភាសាខ្មែរ'],
    ['kn', 'Kannada', 'ಕನ್ನಡ'],
    ['ko', 'Korean', '한국어'],
    ['kr', 'Kanuri', 'Kanuri'],
    ['ks', 'Kashmiri', 'कॉशुर'],
    ['ku', 'Kurdish', 'Kurdî'],
    ['kv', 'Komi', 'коми кыв'],
    ['kw', 'Cornish', 'Kernewek'],
    ['ky', 'Kirghiz', 'Кыргызча'],
    ['la', 'Latin', 'Latine'],
    ['lb', 'Luxembourgish', 'Lëtzebuergesch'],
    ['lg', 'Ganda', 'Luganda'],
    ['li', 'Limburgish', 'Limburgs'],
    ['ln', 'Lingala', 'Lingála'],
    ['lo', 'Lao', 'ພາສາລາວ'],
    ['lt', 'Lithuanian', 'lietuvių kalba'],
    ['lu', 'Luba-Katanga', 'kiluba'],
    ['lv', 'Latvian', 'latviešu valoda'],
    ['mg', 'Malagasy', 'Malagasy fiteny'],
    ['mh', 'Marshallese', 'Kajin M̧ajeļ'],
    ['mi', 'Maori', 'te reo Māori'],
    ['mk', 'Macedonian', 'македонски јазик'],
    ['ml', 'Malayalam', 'മലയാളം'],
    ['mn', 'Mongolian', 'Монгол'],
    ['mr', 'Marathi', 'मराठी'],
    ['ms', 'Malay', 'Bahasa Melayu'],
    ['mt', 'Maltese', 'Malti'],
    ['my', 'Burmese', 'ဗမာစာ'],
    ['na', 'Nauru', 'Ekakairũ Naoero'],
    ['nb', 'Norwegian Bokmål', 'Norsk bokmål'],
    ['nd', 'North Ndebele', 'isiNdebele'],
    ['ne', 'Nepali', 'नेपाली'],
    ['ng', 'Ndonga', 'Owambo'],
    ['nl', 'Dutch', 'Nederlands'],
    ['nn', 'Norwegian Nynorsk', 'Norsk nynorsk'],
    ['no', 'Norwegian', 'Norsk'],
    ['nr', 'South Ndebele', 'isiNdebele'],
    ['nv', 'Navajo', 'Diné bizaad'],
    ['ny', 'Chichewa', 'chiCheŵa'],
    ['oc', 'Occitan', 'occitan'],
    ['oj', 'Ojibwe', 'ᐊᔑᐊᐏᓇᐃᔮ'],
    ['om', 'Oromo', 'Afaan Oromoo'],
    ['or', 'Odia', 'ଓଡ଼ିଆ'],
    ['pa', 'Punjabi', 'ਪੰਜਾਬੀ'],
    ['pl', 'Polish', 'język polski'],
    ['ps', 'Pashto', 'پښتو'],
    ['pt', 'Portuguese', 'português'],
    ['qu', 'Quechua', 'Runa Simi'],
    ['rm', 'Romansh', 'rumantsch grischun'],
    ['rn', 'Kirundi', 'kiRundi'],
    ['ro', 'Romanian', 'română'],
    ['ru', 'Russian', 'русский язык'],
    ['rw', 'Kinyarwanda', 'Ikinyarwanda'],
    ['sa', 'Sanskrit', 'संस्कृतम्'],
    ['sc', 'Sardinian', 'sardu'],
    ['sd', 'Sindhi', 'सिन्धी'],
    ['se', 'Northern Sami', 'Davvisámegiella'],
    ['sg', 'Sango', 'yângâ tî sängö'],
    ['si', 'Sinhala', 'සිංහල'],
    ['sk', 'Slovak', 'slovenčina'],
    ['sl', 'Slovenian', 'slovenščina'],
    ['sm', 'Samoan', "gagana fa'a Samoa"],
    ['sn', 'Shona', 'chiShona'],
    ['so', 'Somali', 'Soomaaliga'],
    ['sq', 'Albanian', 'Shqip'],
    ['sr', 'Serbian', 'српски језик'],
    ['ss', 'Swati', 'SiSwati'],
    ['st', 'Sotho', 'Sesotho'],
    ['su', 'Sundanese', 'Basa Sunda'],
    ['sv', 'Swedish', 'svenska'],
    ['sw', 'Swahili', 'Kiswahili'],
    ['ta', 'Tamil', 'தமிழ்'],
    ['te', 'Telugu', 'తెలుగు'],
    ['tg', 'Tajik', 'тоҷикӣ'],
    ['th', 'Thai', 'ไทย'],
    ['ti', 'Tigrinya', 'ትግርኛ'],
    ['tk', 'Turkmen', 'Türkmen'],
    ['tl', 'Tagalog', 'Tagalog'],
    ['tn', 'Tswana', 'Setswana'],
    ['to', 'Tonga', 'faka Tonga'],
    ['tr', 'Turkish', 'Türkçe'],
    ['ts', 'Tsonga', 'Xitsonga'],
    ['tt', 'Tatar', 'татарча'],
    ['tw', 'Twi', 'Twi'],
    ['ty', 'Tahitian', 'Reo Tahiti'],
    ['ug', 'Uighur', 'Uyƣurqə'],
    ['uk', 'Ukrainian', 'українська мова'],
    ['ur', 'Urdu', 'اردو'],
    ['uz', 'Uzbek', "O'zbek"],
    ['ve', 'Venda', 'Tshivenḓa'],
    ['vi', 'Vietnamese', 'Tiếng Việt'],
    ['vo', 'Volapük', 'Volapük'],
    ['wa', 'Walloon', 'Walon'],
    ['wo', 'Wolof', 'Wolof'],
    ['xh', 'Xhosa', 'isiXhosa'],
    ['yi', 'Yiddish', 'ייִדיש'],
    ['yo', 'Yoruba', 'Yorùbá'],
    ['za', 'Zhuang', 'Sawcuengh'],
    ['zh', 'Chinese', '中文'],
    ['zu', 'Zulu', 'isiZulu'],
]);
function build(tag, englishName, endonym) {
    // normalizeLanguageId = pengesahan BCP-47 enjin; rosegak → throw (gagal pantas).
    const parsed = normalizeLanguageId(tag);
    return Object.freeze({ tag: parsed.primary, englishName, endonym });
}
export const WORLD_LANGUAGES = Object.freeze(ISO_639_1.map(([tag, englishName, endonym]) => build(tag, englishName, endonym)));
const BY_TAG = new Map(WORLD_LANGUAGES.map((entry) => [entry.tag, entry]));
export function findWorldLanguage(tag) {
    if (typeof tag !== 'string') {
        return null;
    }
    const trimmed = tag.trim();
    if (trimmed === '') {
        return null;
    }
    let primary;
    try {
        // Enjin yang menormalkan (termasuk tag lama: tl → fil); UI tidak duplikasi jadual.
        // '_' diterima dahulu (sesetengah pelayar lapor "en_US") kerana BCP-47 pakai '-'.
        primary = normalizeLanguageId(trimmed.replace(/_/g, '-')).primary;
    }
    catch {
        return null;
    }
    return BY_TAG.get(primary) ?? null;
}
/** Label paparan: endonim didahulukan, nama English sebagai penegas. */
export function worldLanguageLabel(entry) {
    return entry.endonym === entry.englishName
        ? entry.englishName
        : `${entry.endonym} · ${entry.englishName}`;
}
//# sourceMappingURL=world-languages.js.map